/* LifeSkillsBrowser — step-by-step browser for the Life Skills path.
   Used verbatim on the user page (/life-skills) AND inside the Admin Panel.
   Navigation: Step 1 Categories → Step 2 Sub-paths → Step 3 Skill cards.
   Every category / sub-path / skill comes from the JSON data (category +
   subcategory fields) — nothing hard-coded, no counts shown anywhere.
   Dark serif theme. Preview text rendered exactly as served. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { recordCardEvent } from '../../lib/clarityCard';
import {
  ArrowLeft, ArrowRight, X, Sparkles, HeartHandshake, IndianRupee,
  AlertTriangle, Users, Route, Wrench, Flag, Info, ChevronRight,
} from 'lucide-react';
import { SkillAddedSections, SharedGuidance } from './LifeSkillsAdded';
import { useNavigate, Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const IMG_BASE = '/lifeskills/';
const PARCHMENT = '#F5E9D4';
const DEEP = '#0B1F2E';
const PANEL = '#1A3548';
const SERIF = '"Fraunces", "Cormorant Garamond", "Georgia", serif';

const INCOME_DISCLAIMER = 'Income figures shown are indicative examples only — actual earnings vary widely by city, skill level, hours, and market conditions. Always verify current local rates before making decisions.';

const TEASER_LABELS = [
  ['why_it_matters', 'Why it matters'],
  ['what_you_learn', 'What you learn'],
  ['how_it_helps', 'How it helps'],
  ['dignity_message', 'Dignity message'],
  ['first_step', 'First step'],
];

const FULL_LABELS = [
  ['what_this_skill_really_is', 'What this skill really is', Sparkles,       'border-teal-200 bg-teal-50/40',       'from-teal-400 to-emerald-500'],
  ['dignity_note',              'Dignity note',              HeartHandshake, 'border-amber-200 bg-amber-50/40',     'from-amber-400 to-orange-500'],
  ['income_reality',            'Income reality',            IndianRupee,    'border-emerald-200 bg-emerald-50/40', 'from-emerald-400 to-teal-500'],
  ['risks_to_know',             'Risks to know',             AlertTriangle,  'border-rose-200 bg-rose-50/40',       'from-rose-400 to-pink-500'],
  ['who_should_learn',          'Who should learn it',       Users,          'border-blue-200 bg-blue-50/40',       'from-blue-400 to-indigo-500'],
  ['learning_path',             'Learning path',             Route,          'border-purple-200 bg-purple-50/40',   'from-purple-400 to-fuchsia-500'],
  ['tools_needed',              'Tools needed',              Wrench,         'border-cyan-200 bg-cyan-50/40',       'from-cyan-400 to-sky-500'],
  ['first_step_this_week',      'First step this week',      Flag,           'border-teal-200 bg-teal-50/40',       'from-teal-400 to-emerald-500'],
];

const FlipCard = ({ skill, onExplore }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`ls-flip ${flipped ? 'ls-flipped' : ''}`}
      onClick={() => setFlipped((f) => !f)}
      data-testid={`ls-card-${skill.global_id}`}
    >
      <div className="ls-flip-inner">
        {/* FRONT */}
        <div className="ls-face ls-front" style={{ background: PANEL }}>
          <img
            src={`${IMG_BASE}${skill.image}`}
            alt={skill.name}
            loading="lazy"
            className="w-full h-40 object-cover"
            style={{ borderRadius: '14px 14px 0 0' }}
          />
          <div className="p-4">
            <span
              className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-2"
              style={{ background: `${PARCHMENT}22`, color: PARCHMENT }}
            >{skill.category}</span>
            <h3 className="font-bold leading-snug" style={{ color: PARCHMENT, fontSize: 16 }}>{skill.name}</h3>
            <p className="mt-2 text-[11px] uppercase tracking-widest" style={{ color: `${PARCHMENT}66` }}>
              Tap to preview
            </p>
          </div>
        </div>
        {/* BACK */}
        <div className="ls-face ls-back" style={{ background: DEEP, border: `1px solid ${PARCHMENT}44` }}>
          <div className="ls-back-scroll p-4">
            {TEASER_LABELS.map(([k, label]) => (
              <div key={k} className="mb-2.5">
                <div className="text-[10px] font-bold uppercase tracking-widest mb-0.5" style={{ color: `${PARCHMENT}88` }}>{label}</div>
                <div className="text-[12.5px] leading-relaxed" style={{ color: PARCHMENT }}>{skill.teaser?.[k]}</div>
              </div>
            ))}
          </div>
          <div className="p-3 pt-1">
          <Link
            to={`/life-skills/skills/${encodeURIComponent(skill.global_id)}`}
            onClick={(e) => { e.stopPropagation(); }}
              className="w-full inline-flex justify-center items-center rounded-full font-semibold"
              style={{
                background: PARCHMENT, color: DEEP, minHeight: 44,
                fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em',
              }}
              data-testid={`ls-explore-${skill.global_id}`}
            >Explore Skill</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LifeSkillDetail = ({ skill, guidance, onClose }) => (
  <div
    className="w-full max-w-3xl mx-auto"
    data-testid="ls-detail-content"
  >
    <div
      className="w-full bg-gray-50"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sticky header — matches other paths' light preview style */}
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-5 md:px-7 pt-5 pb-3 bg-white border-b border-gray-100">
        <div>
          <span className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full mb-1.5 bg-teal-50 text-teal-700">
            {skill.category} · {skill.subcategory}
          </span>
          <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-tight">
            {skill.name}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close"
          className="rounded-full p-2.5 shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
          style={{ minWidth: 44, minHeight: 44 }}
          data-testid="ls-modal-close">
          <X size={18} className="mx-auto" />
        </button>
      </div>

      {/* Full image — square 1254×1254, shown whole, never cropped */}
      <div className="px-4 md:px-6 pt-4">
        <img
          src={`${IMG_BASE}${skill.image}`}
          alt={skill.name}
          loading="lazy"
          className="w-full h-auto rounded-2xl shadow-sm"
          data-testid="ls-modal-image"
        />
      </div>

      {/* 8 sections — same card style as the other paths' full previews */}
      <div className="px-4 md:px-6 py-5 space-y-4">
        {FULL_LABELS.map(([k, label, Icon, cardCls, iconCls]) => (
          <React.Fragment key={k}>
            <section className={`bg-white border-2 ${cardCls} rounded-3xl p-5 md:p-7 shadow-sm`} data-testid={`ls-section-${k}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${iconCls} text-white flex items-center justify-center shrink-0 shadow-sm`}>
                  <Icon size={18} />
                </span>
                <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{label}</h2>
              </div>
              <div className="text-gray-700 text-base leading-relaxed">{skill.full_preview?.[k]}</div>
            </section>
            {k === 'income_reality' && (
              <p className="flex items-start gap-2 px-2 text-xs text-gray-500 leading-relaxed" data-testid="ls-income-disclaimer">
                <Info size={13} className="mt-0.5 shrink-0 text-gray-400" />
                <span>
                  {guidance?.income_policy || INCOME_DISCLAIMER}{' '}
                  <a href="/ai-disclaimer" target="_blank" rel="noreferrer" className="underline underline-offset-2 text-gray-600" data-testid="ls-disclaimer-link">About our AI content</a>
                </span>
              </p>
            )}
          </React.Fragment>
        ))}
        {/* v4.0.0 additions — same card style; shared guidance rendered once per preview */}
        <SkillAddedSections skill={skill} />
        <SharedGuidance guidance={guidance} />
      </div>
    </div>
  </div>
);

/* Breadcrumb — Life Skills → Category → Sub-path. Every earlier segment is tappable. */
const Crumb = ({ trail, onGo }) => (
  <nav className="flex flex-wrap items-center gap-1.5 mb-5" data-testid="ls-breadcrumb" aria-label="Breadcrumb">
    {trail.map((seg, i) => {
      const last = i === trail.length - 1;
      return (
        <React.Fragment key={seg.label}>
          {i > 0 && <ChevronRight size={13} style={{ color: `${PARCHMENT}55` }} />}
          {last ? (
            <span className="text-xs font-bold uppercase tracking-widest px-1 py-2" style={{ color: PARCHMENT }}>{seg.label}</span>
          ) : (
            <button
              type="button"
              onClick={() => onGo(seg.step)}
              className="text-xs font-bold uppercase tracking-widest px-1 rounded"
              style={{ color: `${PARCHMENT}88`, minHeight: 44 }}
              data-testid={`ls-crumb-${seg.step}`}
            >{seg.label}</button>
          )}
        </React.Fragment>
      );
    })}
  </nav>
);

export const LifeSkillsBrowser = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState('cats');        // 'cats' | 'subs' | 'grid'
  const [cat, setCat] = useState(null);            // selected category object (from meta)
  const [skills, setSkills] = useState([]);        // all skills of the selected category
  const [sub, setSub] = useState(null);            // selected sub-path name
  const [modal, setModal] = useState(null);        // full skill for modal
  const [guidance, setGuidance] = useState(null);  // catalog-level shared guidance (v4.0.0)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/lifeskills/meta`)
      .then(async (r) => {
        setMeta(r.data);
        try {
          const saved = JSON.parse(sessionStorage.getItem('lxt-life-skills-position') || 'null');
          const c = r.data.categories?.find(x => x.slug === saved?.category);
          if (c) { setCat(c); const { data } = await axios.get(`${API}/api/lifeskills/skills`, { params: { category: c.slug } }); setSkills(data.skills || []); setSub(saved.sub); setStep(saved.step); }
        } catch { /* Public browsing remains available. */ }
      })
      .catch(() => setError('Could not load Life Skills.'));
    axios.get(`${API}/api/lifeskills/guidance`).then((r) => setGuidance(r.data)).catch(() => {});
  }, []);

  useEffect(() => { try { if (cat) sessionStorage.setItem('lxt-life-skills-position', JSON.stringify({ category: cat.slug, sub, step })); } catch { /* Session-only position is optional. */ } }, [cat, sub, step]);

  /* Step 1 → Step 2 (or straight to Step 3 when only one sub-path) */
  const openCat = async (c) => {
    setCat(c); setSub(null); setSkills([]); setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/api/lifeskills/skills`, { params: { category: c.slug } });
      const list = r.data.skills || [];
      setSkills(list);
      const subs = c.subcategories || [];
      if (subs.length > 1) {
        setStep('subs');
      } else {
        setSub(subs[0] || null);
        setStep('grid');
      }
    } catch { setError('Could not load skills.'); }
    setLoading(false);
  };

  /* Step 2 → Step 3 */
  const openSub = (s) => { setSub(s); setStep('grid'); };

  const goTo = (target) => {
    if (target === 'cats') { setStep('cats'); setCat(null); setSub(null); setSkills([]); }
    else if (target === 'subs') { setStep('subs'); setSub(null); }
  };

  const backFromGrid = () => {
    if (cat && (cat.subcategories || []).length > 1) goTo('subs');
    else goTo('cats');
  };

  const explore = async (skill) => {
    try {
      const r = await axios.get(`${API}/api/lifeskills/skill/${skill.global_id}`);
      navigate(`/life-skills/skills/${encodeURIComponent(skill.global_id)}`);
      recordCardEvent('lifeskill_started', { skill_id: skill.global_id, skill_name: skill.name });
    } catch { setError('Could not load preview.'); }
  };

  /* Sub-path cover image = first skill of that sub-path (pure data, no hard-coding) */
  const subCover = (s) => (skills.find((k) => k.subcategory === s) || {}).image;
  const visible = skills.filter((s) => s.subcategory === sub);

  const trail = [{ label: 'Life Skills', step: 'cats' }];
  if (cat) trail.push({ label: cat.name, step: 'subs' });
  if (step === 'grid' && sub && (cat?.subcategories || []).length > 1) trail.push({ label: sub, step: 'grid' });

  return (
    <div data-testid="lifeskills-browser" style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>
      <style>{`
        .ls-flip { perspective: 1200px; cursor: pointer; height: 340px; }
        .ls-flip-inner { position: relative; width: 100%; height: 100%;
          transition: transform 0.55s cubic-bezier(0.4, 0.1, 0.2, 1); transform-style: preserve-3d; }
        .ls-flip.ls-flipped .ls-flip-inner { transform: rotateY(180deg); }
        @media (hover: hover) { .ls-flip:hover .ls-flip-inner { transform: rotateY(180deg); } }
        .ls-face { position: absolute; inset: 0; backface-visibility: hidden;
          -webkit-backface-visibility: hidden; border-radius: 16px; overflow: hidden;
          display: flex; flex-direction: column; }
        .ls-back { transform: rotateY(180deg); }
        .ls-back-scroll { flex: 1; overflow-y: auto; }
        .ls-clamp1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        @media (prefers-reduced-motion: reduce) { .ls-flip-inner { transition: none; } }
      `}</style>

      {error && <p className="text-center py-6" style={{ color: '#FCA5A5' }} data-testid="ls-error">{error}</p>}

      {(cat || step !== 'cats') && <Crumb trail={trail} onGo={goTo} />}

      {/* ── STEP 1 · Categories (names + one-line description, no counts) ── */}
      {step === 'cats' && meta && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ls-category-grid">
          {meta.categories.map((c) => (
            <button
              key={c.slug} type="button" onClick={() => openCat(c)}
              className="text-left rounded-2xl overflow-hidden transition-transform hover:-translate-y-1"
              style={{ background: PANEL, border: `1px solid ${PARCHMENT}44`, minHeight: 44 }}
              data-testid={`ls-cat-${c.slug}`}
            >
              <img src={`${IMG_BASE}${c.cover}`} alt={c.name} loading="lazy" className="w-full h-32 object-cover" />
              <div className="p-4">
                <h3 className="font-bold" style={{ color: PARCHMENT, fontSize: 16 }}>{c.name}</h3>
                <p className="mt-1 text-xs ls-clamp1" style={{ color: `${PARCHMENT}88` }}>
                  {(c.subcategories || []).length > 1
                    ? c.subcategories.join(' · ')
                    : 'Skills with dignity — honest income, honest risks'}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: PARCHMENT }}>
                  Open <ArrowRight size={12} />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── STEP 2 · Sub-paths of the selected category ── */}
      {step === 'subs' && cat && (
        <div data-testid="ls-subpath-view">
          <button
            type="button"
            onClick={() => goTo('cats')}
            className="inline-flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-widest rounded-full px-4"
            style={{ color: PARCHMENT, border: `1px solid ${PARCHMENT}55`, minHeight: 44 }}
            data-testid="ls-back-btn"
          ><ArrowLeft size={14} /> Back</button>

          <h2 className="font-serif italic mb-1" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(22px,3.5vw,30px)' }}>
            {cat.name}
          </h2>
          <p className="text-sm mb-5" style={{ color: `${PARCHMENT}88` }}>Choose one area to explore.</p>

          {loading && <p className="py-10 text-center text-sm" style={{ color: `${PARCHMENT}88` }}>Loading…</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ls-subpath-grid">
            {(cat.subcategories || []).map((s) => (
              <button
                key={s} type="button" onClick={() => openSub(s)}
                className="text-left rounded-2xl overflow-hidden transition-transform hover:-translate-y-1"
                style={{ background: PANEL, border: `1px solid ${PARCHMENT}44`, minHeight: 44 }}
                data-testid={`ls-sub-${s.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
              >
                {subCover(s) && (
                  <img src={`${IMG_BASE}${subCover(s)}`} alt={s} loading="lazy" className="w-full h-28 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-bold" style={{ color: PARCHMENT, fontSize: 15 }}>{s}</h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: PARCHMENT }}>
                    Open <ArrowRight size={12} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── STEP 3 · Skill cards of the selected sub-path only ── */}
      {step === 'grid' && cat && (
        <div data-testid="ls-category-view">
          <button
            type="button"
            onClick={backFromGrid}
            className="inline-flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-widest rounded-full px-4"
            style={{ color: PARCHMENT, border: `1px solid ${PARCHMENT}55`, minHeight: 44 }}
            data-testid="ls-back-btn"
          ><ArrowLeft size={14} /> Back</button>

          <h2 className="font-serif italic mb-1" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(22px,3.5vw,30px)' }}>
            {(cat.subcategories || []).length > 1 ? sub : cat.name}
          </h2>

          {loading && <p className="py-10 text-center text-sm" style={{ color: `${PARCHMENT}88` }}>Loading skills…</p>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4" data-testid="ls-skill-grid">
            {visible.map((s) => (
              <FlipCard key={s.global_id} skill={s} onExplore={explore} />
            ))}
          </div>
        </div>
      )}

      {modal && <LifeSkillDetail skill={modal} guidance={guidance} onClose={() => setModal(null)} />}
    </div>
  );
};

export default LifeSkillsBrowser;
